import FullLayout from 'layout/FullLayout';
import { useState, useEffect } from 'react';
import { Formik, FieldArray } from 'formik';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import qs from 'qs';
import { useQuery } from '@tanstack/react-query';
import { IconBrandWhatsapp } from '@tabler/icons-react';
import { CircularProgress } from '@mui/material';

import InputText from 'components/InputText';
import InputSelected from 'components/InputSelected';
import ModalAuth from 'components/ModalAuth';
import CartSummary from './ComponentsPage/CartSummary';
import { API_URL, get } from 'lib/network/api';
import { sanitizeParticipants } from 'lib/utils';
import SelectedParticipant from 'pages/Class/ComponentsPage/SelectedParticipant';
import { useCreateInvoiceMutation } from 'pages/Checkout/hook/use-checkout';

const splitName = (fullName) => {
  if (!fullName) return { first_name: '', last_name: '' };
  const parts = fullName.trim().split(' ');
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
};

const OptionParticipant = [
  { id: 0, name: 'Adult', value: 'adult' },
  { id: 1, name: 'Child', value: 'child' },
];

const validationSchema = Yup.object({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string(),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone_number: Yup.string().required('Phone number is required'),
  participant_ids: Yup.array().when('$hasSchedule', {
    is: true,
    then: (schema) => schema.min(1, 'Please select at least one participant or add a new one'),
    otherwise: (schema) => schema.notRequired(),
  }),

  new_participants: Yup.array().notRequired(),
});

const fetchPackageDetail = async ({ queryKey }) => {
  const [_key, { package_id }] = queryKey;
  const endpoint = `${API_URL}/api/event/package/${package_id}/no-login`;
  return await get(endpoint);
};

const fetchDetailSchedule = async ({ queryKey }) => {
  const [_key, { schedule_id }] = queryKey;
  const endpoint = `${API_URL}/api/event/schedule/${schedule_id}/no-login?data_type=class_schedule`;
  return await get(endpoint);
};

const fetchDetailClass = async ({ queryKey }) => {
  const [_key, { class_id }] = queryKey;
  const endpoint = `${API_URL}/api/event/class/${class_id}`;
  return await get(endpoint);
};

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const { mutate: createInvoice, isLoading } = useCreateInvoiceMutation();
  const currentParams = qs.parse(searchParams.toString(), { comma: true });
  const { class_id, schedule_id, package_id, package_type } = currentParams;
  const userProfile = useSelector((state) => state.authProfile);
  const { data } = userProfile;
  const [quantity, setQuantity] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState([]);

  useEffect(() => {
    if (!data) setOpenModal(true);
  }, [data]);

  const { data: packageData } = useQuery({
    queryKey: ['ticketDetail', { package_id }],
    queryFn: fetchPackageDetail,
    enabled: !!package_id,
  });

  const { data: scheduleDetailData } = useQuery({
    queryKey: ['detailSchedule', { schedule_id }],
    queryFn: fetchDetailSchedule,
    enabled: !!schedule_id,
  });

  const { data: classDetailData } = useQuery({
    queryKey: ['detailClass', { class_id }],
    queryFn: fetchDetailClass,
    enabled: !!class_id,
  });

  const nameParts = splitName(data?.name);
  const initialValues = {
    first_name: nameParts.first_name,
    last_name: nameParts.last_name,
    email: data?.email || '',
    phone_number: data?.phone_number || '',
    participant_ids: [],
    new_participants: [
      {
        name: '',
        type: 'child',
        phone_number: '',
      },
    ],
  };

  const handleSelectedParticipant = (participants, setFieldValue) => {
    setSelectedParticipant(participants);
    setFieldValue(
      'participant_ids',
      participants.map((p) => p.id),
    );
  };

  const goContactAdmin = (formValues) => {
    const phoneNumber = '6281290904616';

    // Prepare message with all available information
    const messageParts = [];

    // User information
    messageParts.push(`*Informasi Pengunjung*`);
    messageParts.push(`Nama: ${formValues?.first_name || ''} ${formValues?.last_name || ''}`);
    messageParts.push(`Email: ${formValues?.email || '-'}`);
    messageParts.push(`WhatsApp: ${formValues?.phone_number || '-'}`);

    // Class information
    if (classDetailData?.data?.class) {
      messageParts.push(`\n*Informasi Class*`);
      messageParts.push(`Class: ${classDetailData.data.class.name || '-'}`);
      messageParts.push(`Subject: ${classDetailData.data.class.subject || '-'}`);
      messageParts.push(`Theme: ${classDetailData.data.class.theme || '-'}`);
    }

    // Schedule information
    if (scheduleDetailData?.data?.schedule) {
      messageParts.push(`\n*Jadwal*`);
      messageParts.push(`Tanggal: ${scheduleDetailData.data.schedule.date || '-'}`);
      messageParts.push(`Waktu: ${scheduleDetailData.data.schedule.time || '-'}`);
      if (scheduleDetailData.data.schedule.location) {
        messageParts.push(`Lokasi: ${scheduleDetailData.data.schedule.location}`);
      }
    }

    // Package information
    if (packageData?.package_name) {
      messageParts.push(`\n*Paket*`);
      messageParts.push(`Paket: ${packageData.package_name}`);
      messageParts.push(`Harga: Rp ${packageData?.price?.toLocaleString('id-ID') || '-'}`);
    }

    messageParts.push(`\n*Pesan*`);
    messageParts.push(
      `Saya tertarik dengan class ini dan ingin mendapatkan informasi lebih lanjut.`,
    );

    const message = encodeURIComponent(messageParts.join('\n'));
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

    window.open(whatsappUrl, '_blank');
  };

  const handleOnSubmit = async (values) => {
    if (!packageData) {
      toast.error('Ticket data not available');
      return;
    }

    const participantIds = values.participant_ids || [];
    const filteredParticipants = sanitizeParticipants(values.new_participants);

    const invoicePayload = {
      invoice_req: {
        payment_type: 'doitpay',
        schedule_id: schedule_id || '',
        exhibition_play_id: '',
        type: package_type,
        date: '',
        items: [
          {
            event_package_id: packageData?.id,
            event_package_name: packageData?.package_name,
            quantity,
            participant_ids: participantIds,
            new_participants: filteredParticipants.length > 0 ? filteredParticipants : [],
          },
        ],
      },
      tax_amount: Math.round(packageData.price * quantity * 0.1),
    };

    if (package_type === 'event_package') {
      delete invoicePayload.invoice_req.schedule_id;
      delete invoicePayload.invoice_req.exhibition_play_id;
      delete invoicePayload.invoice_req.date;
    } else if (package_type === 'class') {
      delete invoicePayload.invoice_req.exhibition_play_id;
      delete invoicePayload.invoice_req.date;
    }

    createInvoice(invoicePayload);
  };

  const findValueSelected = (value) => OptionParticipant.find((opt) => opt.value === value) || null;

  return (
    <FullLayout>
      <div className="max-w-7xl mx-auto pt-10 pb-24 px-4 text-kin-text">
        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-10 gap-6">
          <div className="w-full">
            <h2 className="text-xl font-semibold">Your Order Summary</h2>
            <p className="text-base">
              All play passes are valid on the date you have purchased it for.
            </p>

            <div className="mt-6">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validationContext={{ hasSchedule: !!schedule_id }}
                onSubmit={handleOnSubmit}>
                {({ handleSubmit, values, setFieldValue }) => {
                  return (
                    <form className="space-y-6" onSubmit={handleSubmit}>
                      <div>
                        <h3 className="text-lg font-semibold">Contact Information</h3>
                        <div className="space-y-6 mt-2">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <InputText name="first_name" type="text" label="FIRST NAME" />
                            <InputText name="last_name" type="text" label="LAST NAME" />
                          </div>
                          <InputText name="email" type="text" label="EMAIL ADDRESS" />
                          <InputText name="phone_number" type="text" label="WHATSAPP NUMBER" />
                        </div>
                      </div>

                      {/* Participants */}
                      {schedule_id && (
                        <div className="space-y-6">
                          <div>
                            <SelectedParticipant
                              value={selectedParticipant}
                              onChange={(val) => handleSelectedParticipant(val, setFieldValue)}
                            />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">New Participants</h3>
                            <FieldArray name="new_participants">
                              {({ remove }) => (
                                <div className="mt-2 flex flex-col gap-4">
                                  {values.new_participants.map((_, index) => (
                                    <div
                                      key={index}
                                      className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                      <InputText
                                        name={`new_participants[${index}].name`}
                                        label="Name"
                                        type="text"
                                      />

                                      <InputSelected
                                        width="w-full"
                                        label="Participant Type"
                                        name={`new_participants[${index}].type`}
                                        dataOption={OptionParticipant}
                                        selected={findValueSelected(
                                          values.new_participants[index].type,
                                        )}
                                        setSelected={(val) =>
                                          setFieldValue(
                                            `new_participants[${index}].type`,
                                            val?.value || '',
                                          )
                                        }
                                      />

                                      {values.new_participants.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => remove(index)}
                                          className="text-red-500 text-sm underline">
                                          Remove Participant
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </FieldArray>
                          </div>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`${
                          isLoading ? 'bg-gray-300' : 'bg-primary-main'
                        } text-white rounded-[100px] max-w py-2 px-3 flex justify-center items-center`}>
                        {isLoading ? (
                          <CircularProgress style={{ color: '#ffffff' }} size={22} />
                        ) : (
                          'CONFIRM & PAY'
                        )}
                      </button>

                      {/* <button
                        type="button"
                        onClick={() => goContactAdmin(values)}
                        className="bg-[#25D366] mt-6 text-white rounded-[100px] py-3 px-4 w-full sm:w-auto flex justify-center items-center gap-1.5 hover:bg-[#20BA5A] transition-colors">
                        <IconBrandWhatsapp className="w-5 h-5" />
                        <span className="text-xs font-semibold block">BOOK BY WHATSAPP</span>
                      </button> */}
                    </form>
                  );
                }}
              </Formik>

              {openModal && <ModalAuth open={openModal} setOpen={setOpenModal} />}
            </div>
          </div>

          <div className="rounded-2xl bg-white flex flex-col divide-y-[0.5px] divide-kin-text text-kin-text mt-6 lg:mt-0">
            <CartSummary
              scheduleData={scheduleDetailData}
              classData={classDetailData}
              item={packageData}
              quantity={quantity}
              setQuantity={setQuantity}
            />
          </div>
        </div>
      </div>
    </FullLayout>
  );
};

export default CheckoutPage;
